import { MapDrawingConfig } from "../canvas-drawing";
import { drawSelectedRect } from "../canvas-drawing";
import { rectWithPadding } from "../rect-utils";

import {
  PointDrawingConfig,
  SVGIcons,
  offsetForIcon,
  IconSizeDefault,
} from "PosterEditor/common-components/point-icon-drawing/point-icon-drawing";
import { StyledPoint } from "PosterEditor/model/poster-project";
import { MapElement } from "../canvas-drawing";
import { PointCoordinate, Point } from "PosterEditor/model/poster-types";
import { coordinatesToPoints } from "../coordinate-utils";
import { distanceBetweenPoints } from "../coordinate-utils";
import { convertToRGBA } from "../color-utils";
import { EditingPermissions } from "PosterEditor/model/editing-permissions";

export const drawPoints = ({
  map,
  canvasElement,
  originalMapSize,
  scale,
  project,
  pointerLocation,
  pointerInteruptRadius,
  elementToHighlight,
  selectedElement,
  projectEditingPermissions,
  isTemplateEditing,
  imageCache,
}: MapDrawingConfig): {
  elementUnderPointerLocation: MapElement | undefined;
} => {
  const ctx = canvasElement.current.getContext("2d");
  const closestPoint = drawStyledPoints(
    project.styledPoints,
    map,
    ctx,
    scale,
    elementToHighlight,
    pointerLocation,
    pointerInteruptRadius,
    selectedElement?.styledPoint,
    projectEditingPermissions,
    isTemplateEditing,
    imageCache
  );
  if (
    closestPoint?.pointCoordinate &&
    closestPoint.distance < pointerInteruptRadius
  ) {
    const mapElement: MapElement = {
      type: "point",
      pointCoordinate: closestPoint?.pointCoordinate,
      rect: {
        x: 0,
        y: 0,
        width: 10,
        height: 10,
      },
    };
    return { elementUnderPointerLocation: mapElement };
  }
  return { elementUnderPointerLocation: undefined };
};

const drawStyledPoints = (
  styledCoordinates: StyledPoint[],
  map: any,
  ctx: any,
  scale: number,
  elementToHighlight: MapElement | undefined,
  cursorPosition: Point | undefined,
  pointerInteruptRadius: number,
  selectedPoint: StyledPoint | undefined,
  projectEditingPermissions: EditingPermissions,
  isTemplateEditing: boolean = false,
  imageCache?: Map<string, HTMLImageElement>
):
  | {
      closestToCursor: StyledPoint;
      distance: number;
      pointCoordinate: PointCoordinate;
    }
  | undefined => {
  if (!styledCoordinates) return;
  const coordinates = styledCoordinates.map((point) => point.coordinate);
  const points = coordinatesToPoints(map, scale, coordinates);

  let closestToCursor: StyledPoint | undefined;
  let pointCoordinate: PointCoordinate | undefined;
  let distance = Number.MAX_VALUE;

  let pointToHighlight: Point | undefined;

  if (cursorPosition) {
    points.forEach((point) => {
      const styledCoordinate = styledCoordinates[points.indexOf(point)];
      const allowHighlight =
        isTemplateEditing ||
        Object.values(
          styledCoordinate.editingPermissions ?? projectEditingPermissions.point
        ).some(Boolean);

      if (allowHighlight) {
        const distanceToCursor = distanceBetweenPoints(point, cursorPosition);
        if (
          distanceToCursor < distance &&
          distanceToCursor < pointerInteruptRadius
        ) {
          pointToHighlight = point;
          distance = distanceToCursor;
          closestToCursor = styledCoordinate;
          pointCoordinate = {
            point: point,
            coordinate: styledCoordinate.coordinate,
          };
        }
      }
    });
  }

  points.forEach((point) => {
    const styledCoordinate = styledCoordinates[points.indexOf(point)];
    const config: DrawImageConfig = {
      ctx,
      scale,
      elementToHighlight,
      isTemplateEditing,
      projectEditingPermissions,
      point,
      styledPoint: styledCoordinate,
      pointToHighlight,
      selectedPoint,
      imageCache,
    };
    if (styledCoordinate.style.showPoint === true) {
      if (styledCoordinate.style.imageName) {
        drawImage(config);
      } else if (styledCoordinate.style.icon) {
        drawIcon(config);
      }
    }
  });
  return closestToCursor && pointCoordinate
    ? { closestToCursor, distance, pointCoordinate }
    : undefined;
};

interface DrawImageConfig {
  ctx: CanvasRenderingContext2D;
  scale: number;
  elementToHighlight: MapElement | undefined;
  isTemplateEditing: boolean;
  projectEditingPermissions: EditingPermissions;
  point: Point;
  styledPoint: StyledPoint;
  pointToHighlight: Point | undefined;
  selectedPoint: StyledPoint | undefined;
  imageCache?: Map<string, HTMLImageElement>;
}

const drawImage = (config: DrawImageConfig) => {
  const {
    ctx,
    scale,
    elementToHighlight,
    isTemplateEditing,
    projectEditingPermissions,
    point,
    styledPoint,
    pointToHighlight,
    selectedPoint,
    imageCache,
  } = config;

  const isSelected = selectedPoint?.coordinate === styledPoint.coordinate;
  const imageName = styledPoint.style.imageName;

  if (!imageName) {
    return;
  }
  const imageElement = imageCache?.get(imageName);
  if (!imageElement) {
    return;
  }
  const size = styledPoint.style.size * scale;

  ctx.save();
  ctx.drawImage(
    imageElement,
    point.x - size / 2,
    point.y - size / 2,
    size,
    size
  );
  ctx.clip();
  ctx.restore();

  if (isSelected) {
    drawSelectedRect(
      rectWithPadding(
        {
          x: point.x - styledPoint.style.size / 2,
          y: point.y - styledPoint.style.size / 2,
          width: styledPoint.style.size,
          height: styledPoint.style.size,
        },
        5
      ),
      ctx
    );
  }
};

const drawIcon = (config: DrawImageConfig) => {
  const {
    ctx,
    scale,
    elementToHighlight,
    isTemplateEditing,
    projectEditingPermissions,
    point,
    styledPoint: styledCoordinate,
    pointToHighlight,
    selectedPoint,
  } = config;
  if (styledCoordinate.style.icon) {
    const iconOffset = offsetForIcon(
      styledCoordinate.style.icon,
      styledCoordinate.style.size * scale
    );
    const localIconScale =
      (styledCoordinate.style.size / IconSizeDefault) * scale;
    //const isPointHighlighted = elementToHighlight?.type === "point" && elementToHighlight.pointCoordinate.coordinate === styledCoordinate.coordinate
    const isOtherElementHighlighted =
      elementToHighlight?.type === "mapLabel" ||
      elementToHighlight?.type === "staticLabel";
    const mainColor = isOtherElementHighlighted
      ? convertToRGBA(styledCoordinate.style.mainColor, 0.2)
      : styledCoordinate.style.mainColor;
    const secondaryColor = isOtherElementHighlighted
      ? convertToRGBA(styledCoordinate.style.secondaryColor ?? "FFFFFF", 0.2)
      : styledCoordinate.style.secondaryColor;

    const allowHighlight =
      isTemplateEditing ||
      Object.values(
        styledCoordinate.editingPermissions ?? projectEditingPermissions.point
      ).some(Boolean);

    const config: PointDrawingConfig = {
      ctx,
      color: mainColor,
      secondaryColor: secondaryColor,
      scale:
        localIconScale *
        (allowHighlight && pointToHighlight === point ? 1.1 : 1),
      offsetX: point.x + iconOffset.x,
      offsetY: point.y + iconOffset.y,
    };
    const isSelected =
      selectedPoint?.coordinate === styledCoordinate.coordinate;

    if (isSelected) {
      drawSelectedRect(
        rectWithPadding(
          {
            x: point.x + iconOffset.x,
            y: point.y + iconOffset.y,
            width: styledCoordinate.style.size,
            height: styledCoordinate.style.size,
          },
          5
        ),
        ctx
      );
    }
    SVGIcons[styledCoordinate.style.icon].draw(config);
  }
};
